import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Colors, useColors } from "../constants/Colors";
import LightBox from "../components/LightBox";
import Tag from "../components/Tag";
import { FlatProfile, Room } from "../constants/Interfaces";
import {
  trpcReact,
  queryClient,
  setTurnstileToken,
  clearTurnstileToken,
} from "../trpc_client";
import { useTurnstile } from "../hooks/useTurnstile";
import TurnstileWidget from "../components/TurnstileWidget";
import FlatAttributesDisplay from "../components/FlatAttributesDisplay";
import { usePostHog } from "posthog-js/react";
import {
  GoogleMap,
  useJsApiLoader,
  OverlayViewF,
  OverlayView,
} from "@react-google-maps/api";
import { MAP_STYLES, GOOGLE_MAPS_API_KEY } from "../constants/MapStyles";
import { useAuth } from "../contexts/AuthContext";
import AuthModal from "../components/AuthModal";
import ShareModal from "../components/ShareModal";
import TranslateButton from "../components/TranslateButton";
import { useSidebar } from "../contexts/SidebarContext";
import { useUpload } from "../contexts/UploadContext";
import { useProfile } from "../contexts/ProfileContext";

const MAX_AGE = 99;
const GOOGLE_MAPS_MAP_ID = "DEMO_MAP_ID";

type NumericLike = number | string | null | undefined;

interface FlatAttributeObject {
  attribute?: string | number;
}

type FlatAttributeValue = string | number | FlatAttributeObject | null;

interface RoomInput {
  flat_id: number;
  room_id: number;
  rent?: NumericLike;
  utilities?: NumericLike;
  equipment_fees?: NumericLike;
  deposit?: NumericLike;
  size?: NumericLike;
  permanent?: boolean;
  from_date?: string | number | Date | null;
  to_date?: string | number | Date | null;
  looking_for_age_range?: { min: number; max: number } | number[] | null;
  looking_for?: string[];
  furnishing?: string;
  subsidised?: boolean;
}

interface FlatInput {
  flat_id: number;
  name: string;
  member_count: number;
  public: boolean;
  size?: NumericLike;
  flat_text?: { description?: string } | string | null;
  flat_question?: string | null;
  flat_attributes?: FlatAttributeValue[];
  location?: string | null;
  room?: RoomInput[];
  created_at?: string | Date | null;
}

interface FlatCoordinatesInput {
  latitude?: number;
  longitude?: number;
}

interface FlatQueryData {
  flat?: FlatInput;
  coordinates?: FlatCoordinatesInput;
  urls?: string[];
}

interface PassedFlatData {
  room: RoomInput & { flat: FlatInput };
  urls?: string[];
}

const toNumber = (value: NumericLike): number => Number(value) || 0;

const toISOString = (
  date: string | number | Date | null | undefined,
): string | undefined => (date ? new Date(date).toISOString() : undefined);

function transformRoom(room: RoomInput): Room {
  const ageRange = room.looking_for_age_range;
  return {
    flat_id: room.flat_id,
    room_id: room.room_id,
    rent: toNumber(room.rent),
    utilities: toNumber(room.utilities),
    equipment_fees: toNumber(room.equipment_fees),
    deposit: toNumber(room.deposit),
    size: toNumber(room.size),
    permanent: Boolean(room.permanent),
    from_date: toISOString(room.from_date),
    to_date: toISOString(room.to_date),
    looking_for_age_range: Array.isArray(ageRange)
      ? { min: ageRange[0] ?? 0, max: ageRange[1] ?? MAX_AGE }
      : ageRange || { min: 0, max: MAX_AGE },
    looking_for: room.looking_for || [],
    furnishing: room.furnishing || "unfurnished",
    subsidised: room.subsidised || false,
  };
}

function transformFlatData(data: FlatQueryData): FlatProfile | null {
  if (!data?.flat) return null;

  const { flat, coordinates, urls } = data;
  const location = JSON.parse(flat.location || "{}") as {
    place_id?: number;
    lat?: string | number;
    lon?: string | number;
    display_name?: string;
    currency?: string;
    address?: FlatProfile["location"]["address"];
  };
  const flatText =
    typeof flat.flat_text === "string"
      ? flat.flat_text
      : flat.flat_text?.description || "";

  return {
    flat_id: flat.flat_id,
    name: flat.name,
    latitude: coordinates?.latitude || 0,
    longitude: coordinates?.longitude || 0,
    member_count: flat.member_count,
    public: flat.public,
    size: toNumber(flat.size),
    flat_text: flatText,
    flat_question: flat.flat_question || "",
    flat_attributes: flat.flat_attributes || [],
    flat_objects: urls || [],
    created_at: toISOString(flat.created_at) || new Date().toISOString(),
    has_applied: false,
    location: {
      place_id: location.place_id || 0,
      lat: String(coordinates?.latitude || location.lat || ""),
      lon: String(coordinates?.longitude || location.lon || ""),
      display_name: location.display_name || flat.location || "",
      currency: location.currency || "CHF",
      address: location.address || {},
    },
    room: (flat.room || []).map(transformRoom),
  };
}

interface FlatMember {
  user_id: number;
  username: string;
  profile_image: string | null;
}

const REPORT_REASONS = [
  { key: "fake_spam_scam", label: "Fake / spam / scam" },
  { key: "inappropriate", label: "Inappropriate content" },
  { key: "harassment", label: "Harassment" },
  { key: "other", label: "Other" },
] as const;

const FlatView: React.FC = () => {
  const {
    containerNeutralSolidSurface,
    containerTertiarySolidLow,
    containerNeutralLightMedium,
    containerNeutralLightLowest,
    accentColor,
    textNeutralDefault,
    textNeutralMuted,
    textNeutralOnPrimary,
    textTertiaryOnLow,
  } = useColors();
  const { flatId } = useParams<{ flatId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Get flat data passed from navigation (for faster initial render)
  const passedFlatData = (
    location.state as { flatData?: PassedFlatData } | null
  )?.flatData;
  const posthog = usePostHog();
  const { t } = useTranslation();
  const { user, resendVerificationEmail } = useAuth();
  const { isCollapsed } = useSidebar();
  const {
    startUpload,
    updateProgress,
    completeUpload,
    failUpload,
    showUploadToast,
  } = useUpload();
  const { isProfileComplete, needsOnboarding, userExistsInDb } = useProfile();
  const {
    hasError: turnstileError,
    resetToken: resetTurnstileToken,
    siteKey,
    widgetRef,
    onExpire: onTurnstileExpire,
    onError: onTurnstileError,
  } = useTurnstile();
  const [showTurnstileModal, setShowTurnstileModal] = useState(false);
  const [pendingApplicationType, setPendingApplicationType] = useState<
    "text" | "video" | null
  >(null);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [showSafetyMenu, setShowSafetyMenu] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedReportReason, setSelectedReportReason] = useState<
    (typeof REPORT_REASONS)[number]["key"] | null
  >(null);
  const [customReportReason, setCustomReportReason] = useState("");
  const [safetyActionMessage, setSafetyActionMessage] = useState<string | null>(
    null,
  );

  const [currentScrollIndex, setCurrentScrollIndex] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);
  const detailsSectionRef = useRef<HTMLElement>(null);
  const locationSectionRef = useRef<HTMLElement>(null);
  const safetyMenuRef = useRef<HTMLDivElement>(null);

  // Translation state - initialize from localStorage if available
  const [translatedText, setTranslatedText] = useState<string | null>(() => {
    if (typeof window !== "undefined" && flatId) {
      return localStorage.getItem(`translation_${flatId}_text`);
    }
    return null;
  });
  const [isTranslating, setIsTranslating] = useState(false);
  const [showTranslation, setShowTranslation] = useState(() => {
    if (typeof window !== "undefined" && flatId) {
      return localStorage.getItem(`translation_${flatId}_show`) === "true";
    }
    return false;
  });
  const [translationError, setTranslationError] = useState<string | null>(null);
  const [showLanguageSelector, setShowLanguageSelector] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<string>(() => {
    if (typeof window !== "undefined" && flatId) {
      return localStorage.getItem(`translation_${flatId}_lang`) || "en";
    }
    return "en";
  });
  const [map, setMap] = useState<google.maps.Map | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        safetyMenuRef.current &&
        !safetyMenuRef.current.contains(event.target as Node)
      ) {
        setShowSafetyMenu(false);
      }
    };

    if (showSafetyMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSafetyMenu]);

  // Persist translation state to localStorage
  useEffect(() => {
    if (flatId && translatedText) {
      localStorage.setItem(`translation_${flatId}_text`, translatedText);
      localStorage.setItem(`translation_${flatId}_lang`, selectedLanguage);
    }
  }, [flatId, translatedText, selectedLanguage]);

  useEffect(() => {
    if (flatId) {
      localStorage.setItem(
        `translation_${flatId}_show`,
        String(showTranslation),
      );
    }
  }, [flatId, showTranslation]);

  // Translate text using Google Translate API
  const translateText = useCallback(
    async (text: string, targetLang: string) => {
      if (!text || text.trim() === "") return;

      setIsTranslating(true);
      setTranslationError(null);
      setShowLanguageSelector(false);

      try {
        const response = await fetch(
          `https://translation.googleapis.com/language/translate/v2?key=${GOOGLE_MAPS_API_KEY}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              q: text,
              target: targetLang,
            }),
          },
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          if (response.status === 403) {
            throw new Error("API_NOT_ENABLED");
          }
          throw new Error(errorData?.error?.message || "Translation failed");
        }

        const data = await response.json();
        const translated = data.data.translations[0].translatedText;

        // Decode HTML entities that Google Translate API returns
        const textarea = document.createElement("textarea");
        textarea.innerHTML = translated;
        const decodedText = textarea.value;

        setTranslatedText(decodedText);
        setShowTranslation(true);
        setSelectedLanguage(targetLang);
      } catch (error) {
        console.error("Translation error:", error);
        if (error instanceof Error && error.message === "API_NOT_ENABLED") {
          setTranslationError(
            t("translation_api_not_enabled") ||
              "Translation API is not enabled. Please enable the Cloud Translation API in Google Cloud Console.",
          );
        } else {
          setTranslationError(
            t("translation_error") || "Translation failed. Please try again.",
          );
        }
      } finally {
        setIsTranslating(false);
      }
    },
    [t],
  );

  // Download app modal – shown once per device on first mobile apply
  const [showDownloadAppModal, setShowDownloadAppModal] = useState(false);

  // Application state
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [showVerifyEmailModal, setShowVerifyEmailModal] = useState(false);
  const [verifyEmailSent, setVerifyEmailSent] = useState(false);
  const [verifyEmailError, setVerifyEmailError] = useState<string | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [applicationText, setApplicationText] = useState("");
  const [applicationSuccess, setApplicationSuccess] = useState(false);
  const [applicationType, setApplicationType] = useState<"text" | "video">(
    "text",
  );

  // Video application state
  const [isRecording, setIsRecording] = useState(false);
  const [recordedVideo, setRecordedVideo] = useState<Blob | null>(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState<string | null>(null);
  const [recordedMimeType, setRecordedMimeType] = useState<string>("video/mp4");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recordingStreamRef = useRef<MediaStream | null>(null);
  const sourceVideoRef = useRef<HTMLVideoElement | null>(null);
  const drawFrameRef = useRef<number | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const recordingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Draggable modal state
  const [modalPos, setModalPos] = useState({ x: 0, y: 0 });
  const [hasUserPositioned, setHasUserPositioned] = useState(false);
  const [isModalDragging, setIsModalDragging] = useState(false);
  const applyModalRef = useRef<HTMLDivElement>(null);
  const modalDragRef = useRef<{
    startX: number;
    startY: number;
    startPosX: number;
    startPosY: number;
  } | null>(null);
  const modalPosRef = useRef({ x: 0, y: 0 });
  const modalRafRef = useRef<number | null>(null);

  // Fetch user's applications to check if already applied
  const userApplicationsQuery = trpcReact.getApplicationsByUser.useQuery(
    undefined,
    { enabled: userExistsInDb },
  );

  // Fetch user's own flats to check if this is their flat
  const userFlatsQuery = trpcReact.getUserFlats.useQuery(undefined, {
    enabled: userExistsInDb,
  });

  // Create object mutation for video upload
  const createObjectMutation = trpcReact.createObject.useMutation();

  // Text application mutation
  const createTextApplicationMutation =
    trpcReact.createTextApplication.useMutation({
      onSuccess: () => {
        setApplicationSuccess(true);
        setApplicationText("");
        posthog?.capture("application_submitted", {
          flat_id: flatId,
          type: "text",
        });
        userApplicationsQuery.refetch();
      },
      onError: (error: { message?: string }) => {
        console.error("Text application error:", error);
        alert(
          error.message || "Failed to submit application. Please try again.",
        );
      },
    });

  // Video application mutation
  const createVideoApplicationMutation =
    trpcReact.createVideoApplication.useMutation({
      onSuccess: () => {
        setApplicationSuccess(true);
        setRecordedVideo(null);
        setVideoPreviewUrl(null);
        posthog?.capture("application_submitted", {
          flat_id: flatId,
          type: "video",
        });
        userApplicationsQuery.refetch();
      },
      onError: (error: { message?: string }) => {
        console.error("Video application error:", error);
        alert(
          error.message ||
            "Failed to submit video application. Please try again.",
        );
        setIsUploading(false);
      },
    });

  // Load Google Maps API
  const { isLoaded: isMapLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  });

  const handleScroll = () => {
    if (carouselRef.current) {
      const { scrollLeft, clientWidth } = carouselRef.current;
      const newIndex = Math.round(scrollLeft / clientWidth);
      setCurrentScrollIndex(newIndex);
    }
  };
  useEffect(() => {
    document.body.style.backgroundColor = containerNeutralSolidSurface;
    document.documentElement.style.backgroundColor =
      containerNeutralSolidSurface;

    return () => {
      document.body.style.backgroundColor = "";
      document.documentElement.style.backgroundColor = "";
    };
  }, [containerNeutralSolidSurface]);

  const flatIdNum = flatId ? parseInt(flatId, 10) : 0;
  const data = trpcReact.getflat.useQuery(
    { flat_id: flatIdNum },
    {
      enabled: !!flatId && !isNaN(flatIdNum) && flatIdNum > 0,
    },
  );

  // Fetch flat members (only when authenticated)
  const flatMembersQuery = trpcReact.getUserByFlatId.useQuery(
    { flat_id: flatIdNum },
    {
      enabled: userExistsInDb && !!flatId && !isNaN(flatIdNum) && flatIdNum > 0,
    },
  );
  const { data: currentUserProfile } = trpcReact.getCurrentUserProfile.useQuery(
    undefined,
    { enabled: userExistsInDb },
  );

  const blockUsersMutation = trpcReact.blockUsers.useMutation();
  const reportUsersMutation = trpcReact.reportUsers.useMutation();

  // Transform passed flat data from BrowseFlats for initial render
  const initialFlatData = useMemo(() => {
    if (!passedFlatData) return null;
    const { room, urls } = passedFlatData;
    const flatInfo = room.flat;
    const location = JSON.parse(flatInfo.location || "{}");
    const flatText =
      typeof flatInfo.flat_text === "string"
        ? flatInfo.flat_text
        : flatInfo.flat_text?.description || "";

    return {
      flat_id: flatInfo.flat_id,
      name: flatInfo.name,
      latitude: parseFloat(location.lat) || 0,
      longitude: parseFloat(location.lon) || 0,
      member_count: flatInfo.member_count,
      public: flatInfo.public,
      size: toNumber(flatInfo.size),
      flat_text: flatText,
      flat_question: flatInfo.flat_question || "",
      flat_attributes: flatInfo.flat_attributes || [],
      flat_objects: urls || [],
      created_at: flatInfo.created_at || new Date().toISOString(),
      has_applied: false,
      location: {
        place_id: location.place_id || 0,
        lat: String(location.lat || ""),
        lon: String(location.lon || ""),
        display_name: location.display_name || "",
        currency: location.currency || "CHF",
        address: location.address || {},
      },
      room: [
        {
          flat_id: room.flat_id,
          room_id: room.room_id,
          rent: toNumber(room.rent),
          utilities: toNumber(room.utilities),
          equipment_fees: toNumber(room.equipment_fees),
          deposit: toNumber(room.deposit),
          size: toNumber(room.size),
          permanent: room.permanent,
          from_date: room.from_date
            ? new Date(room.from_date).toISOString()
            : undefined,
          to_date: room.to_date
            ? new Date(room.to_date).toISOString()
            : undefined,
          looking_for_age_range: room.looking_for_age_range || {
            min: 0,
            max: MAX_AGE,
          },
          looking_for: room.looking_for || [],
          furnishing: room.furnishing || "unfurnished",
          subsidised: room.subsidised || false,
        },
      ],
    } as FlatProfile;
  }, [passedFlatData]);

  const flat = useMemo(() => {
    // Use fetched data if available, otherwise use passed data for faster initial render
    if (data.data) {
      return transformFlatData(data.data);
    }
    return initialFlatData;
  }, [data.data, initialFlatData]);

  // Stable images reference - only updates when URLs actually change
  const imagesRef = useRef<string[]>([]);
  const stableImages = useMemo(() => {
    const newImages = flat?.flat_objects ?? [];
    const currentImages = imagesRef.current;

    // Check if URLs are the same
    const urlsMatch =
      currentImages.length === newImages.length &&
      currentImages.every((url, i) => url === newImages[i]);

    if (!urlsMatch) {
      imagesRef.current = newImages;
      return newImages;
    }
    return currentImages;
  }, [flat?.flat_objects]);

  // Saved flats functionality
  const { data: savedFlats, refetch: refetchSavedFlats } =
    trpcReact.getSavedFlats.useQuery(undefined, {
      enabled: userExistsInDb,
    });

  const saveFlatMutation = trpcReact.saveFlat.useMutation({
    onSuccess: () => refetchSavedFlats(),
  });

  const unSaveFlatMutation = trpcReact.unSaveFlat.useMutation({
    onSuccess: () => refetchSavedFlats(),
  });

  const isFlatSaved = useMemo(() => {
    if (!savedFlats || !flat?.room?.[0]?.room_id) return false;
    return savedFlats.some(
      (saved) => saved.flat?.room?.[0]?.room_id === flat.room[0].room_id,
    );
  }, [savedFlats, flat]);

  const handleToggleSave = () => {
    const roomId = flat?.room?.[0]?.room_id;
    if (!roomId) return;

    if (isFlatSaved) {
      unSaveFlatMutation.mutate({ room_id: roomId });
    } else {
      saveFlatMutation.mutate({ room_id: roomId });
    }
  };

  const error = data.error ? data.error.message : null;
  // Only show loading if we don't have any data (neither from query nor from navigation state)
  const isLoading = data.isLoading && !flat;

  const openApp = () => {
    if (!flatId) return;
    window.location.href = `com.flateroo.flateroo://app/flat-view/${flatId}`;
  };

  useEffect(() => {
    const isIphone = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    const isAndroid = /Android/i.test(navigator.userAgent);
    if (isIphone || isAndroid) {
      openApp();
    }
  }, []);

  const room = flat?.room?.[0];

  // Check if user has already applied to this flat's room
  const hasAlreadyApplied = useMemo(() => {
    if (!room || !userApplicationsQuery.data) return false;
    return userApplicationsQuery.data.some(
      (application) => application.room_id === room.room_id,
    );
  }, [room, userApplicationsQuery.data]);

  // Check if this is the user's own flat
  const isOwnFlat = useMemo(() => {
    if (!flat || !userFlatsQuery.data) return false;
    return userFlatsQuery.data.some(
      (userFlat) => userFlat.flat.flat_id === flat.flat_id,
    );
  }, [flat, userFlatsQuery.data]);

  const safetyTargetUserIds = useMemo(() => {
    const flatMembers =
      (flatMembersQuery.data as FlatMember[] | undefined) ?? [];
    const currentUserId = currentUserProfile?.user_id;
    return flatMembers
      .map((member) => member.user_id)
      .filter((memberId) => memberId !== currentUserId && memberId !== 1);
  }, [flatMembersQuery.data, currentUserProfile?.user_id]);

  const handleBlockFlatmates = async () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    if (!safetyTargetUserIds.length) return;
    try {
      await blockUsersMutation.mutateAsync(
        safetyTargetUserIds.map((userId) => ({
          blocked_user_id: userId,
          reason: "User blocked",
        })),
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [["getChats"]] }),
        queryClient.invalidateQueries({
          queryKey: [["getApplicationsByUser"]],
        }),
        queryClient.invalidateQueries({ queryKey: [["getRoomApplications"]] }),
        queryClient.invalidateQueries({ queryKey: [["publicGetRooms"]] }),
      ]);
      setSafetyActionMessage("Flatmates blocked.");
      setShowSafetyMenu(false);
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Failed to block flatmates:", error);
      setSafetyActionMessage("Could not block flatmates. Please try again.");
    }
  };

  const handleOpenReportModal = () => {
    setShowSafetyMenu(false);
    setSelectedReportReason(null);
    setCustomReportReason("");
    setShowReportModal(true);
  };

  const handleSubmitReport = async () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    if (!safetyTargetUserIds.length) return;
    if (!selectedReportReason) {
      setSafetyActionMessage("Please select a reason for reporting.");
      return;
    }
    if (selectedReportReason === "other" && !customReportReason.trim()) {
      setSafetyActionMessage("Please provide a custom reason.");
      return;
    }
    const reasonToSubmit =
      selectedReportReason === "other"
        ? customReportReason.trim()
        : selectedReportReason;

    try {
      await reportUsersMutation.mutateAsync(
        safetyTargetUserIds.map((userId) => ({
          reported_user_id: userId,
          reason: reasonToSubmit,
        })),
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [["getChats"]] }),
        queryClient.invalidateQueries({
          queryKey: [["getApplicationsByUser"]],
        }),
        queryClient.invalidateQueries({ queryKey: [["getRoomApplications"]] }),
        queryClient.invalidateQueries({ queryKey: [["publicGetRooms"]] }),
      ]);
      setSafetyActionMessage("Flatmates reported and blocked.");
      setShowReportModal(false);
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Failed to report flatmates:", error);
      setSafetyActionMessage("Could not report flatmates. Please try again.");
    }
  };

  const pendingApplyTypeRef = useRef<"video" | "text" | undefined>(undefined);

  const handleApplyClick = (type?: "video" | "text") => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    if (!user.emailVerified) {
      setShowVerifyEmailModal(true);
      setVerifyEmailSent(false);
      setVerifyEmailError(null);
      return;
    }
    if (!isProfileComplete) {
      if (needsOnboarding) {
        navigate("/signup");
      } else {
        navigate("/profile/edit");
      }
      return;
    }
    if (hasAlreadyApplied) return;

    const isMobileDevice = window.innerWidth < 1024;
    const hasSeenDownloadModal = false;

    if (isMobileDevice && !hasSeenDownloadModal) {
      pendingApplyTypeRef.current = type;
      setShowDownloadAppModal(true);
      localStorage.setItem("flateroo_seen_download_app_modal", "1");
      posthog?.capture("download_app_modal_shown", { flat_id: flatId });
      return;
    }

    if (type) {
      setApplicationType(type);
    }
    setShowApplyModal(true);
  };

  const handleContinueOnWeb = () => {
    setShowDownloadAppModal(false);
    const type = pendingApplyTypeRef.current;
    if (type) {
      setApplicationType(type);
    }
    setShowApplyModal(true);
  };



  const handleResendVerification = async () => {
    setVerifyEmailError(null);
    try {
      await resendVerificationEmail();
      setVerifyEmailSent(true);
    } catch (error: any) {
      console.error("Failed to send verification email:", error);
      if (error?.code === "auth/too-many-requests") {
        setVerifyEmailError(
          t("verify_email_too_many") ||
            "Too many requests. Please try again later.",
        );
      } else {
        setVerifyEmailError(
          t("verify_email_error") ||
            "Failed to send verification email. Please try again.",
        );
      }
    }
  };

  const handleRefreshVerification = async () => {
    if (!user) return;
    await user.reload();
    if (user.emailVerified) {
      setShowVerifyEmailModal(false);
    } else {
      setVerifyEmailError(
        t("verify_email_not_yet") ||
          "Email not verified yet. Please check your inbox.",
      );
    }
  };

  const handleSubmitTextApplication = () => {
    if (!room || !applicationText.trim()) return;
    resetTurnstileToken();
    setPendingApplicationType("text");
    setShowTurnstileModal(true);
  };

  const doSubmitTextApplication = (token: string) => {
    if (!room || !applicationText.trim()) return;
    setTurnstileToken(token);
    createTextApplicationMutation.mutate(
      {
        room_id: room.room_id,
        text_application_answer: applicationText.trim(),
      },
      {
        onSettled: () => {
          clearTurnstileToken();
          resetTurnstileToken();
        },
      },
    );
  };

  const stopActiveRecordingStreams = () => {
    if (drawFrameRef.current !== null) {
      cancelAnimationFrame(drawFrameRef.current);
      drawFrameRef.current = null;
    }

    if (recordingStreamRef.current) {
      recordingStreamRef.current.getTracks().forEach((track) => track.stop());
      recordingStreamRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    sourceVideoRef.current = null;
  };

  // Helper to get getUserMedia across browsers
  const getMediaStream = async (
    constraints: MediaStreamConstraints,
  ): Promise<MediaStream> => {
    if (navigator.mediaDevices?.getUserMedia) {
      return navigator.mediaDevices.getUserMedia(constraints);
    }
    // Legacy fallback for older browsers
    const legacyGetUserMedia =
      (navigator as any).getUserMedia ||
      (navigator as any).webkitGetUserMedia ||
      (navigator as any).mozGetUserMedia ||
      (navigator as any).msGetUserMedia;
    if (legacyGetUserMedia) {
      return new Promise((resolve, reject) => {
        legacyGetUserMedia.call(navigator, constraints, resolve, reject);
      });
    }
    throw new DOMException(
      "getUserMedia is not supported in this browser or requires HTTPS.",
      "NotSupportedError",
    );
  };

  // Video recording functions
  const startRecording = async () => {
    try {
      stopActiveRecordingStreams();

      // Try to get camera+audio with ideal constraints, fall back to simpler ones
      let stream: MediaStream;
      try {
        stream = await getMediaStream({
          video: {
            facingMode: "user",
            width: { ideal: 720 },
            height: { ideal: 1280 },
            aspectRatio: { ideal: 9 / 16 },
          },
          audio: true,
        });
      } catch (constraintError) {
        // If the detailed constraints fail, try with simple constraints
        console.warn(
          "Detailed constraints failed, trying simple constraints:",
          constraintError,
        );
        try {
          stream = await getMediaStream({
            video: { facingMode: "user" },
            audio: true,
          });
        } catch {
          // Last resort: just ask for video + audio with no constraints
          stream = await getMediaStream({ video: true, audio: true });
        }
      }
      streamRef.current = stream;

      // Set recording state first so the video element renders
      setIsRecording(true);

      // Prioritize MP4 format (works in Safari and some Chrome versions)
      // Fall back to WebM if MP4 is not supported
      let mimeType: string;
      if (MediaRecorder.isTypeSupported("video/mp4;codecs=avc1")) {
        mimeType = "video/mp4;codecs=avc1";
      } else if (MediaRecorder.isTypeSupported("video/mp4")) {
        mimeType = "video/mp4";
      } else if (MediaRecorder.isTypeSupported("video/webm;codecs=vp9")) {
        mimeType = "video/webm;codecs=vp9";
      } else if (MediaRecorder.isTypeSupported("video/webm;codecs=vp8")) {
        mimeType = "video/webm;codecs=vp8";
      } else {
        mimeType = "video/webm";
      }

      console.log(`Recording with mimeType: ${mimeType}`);
      setRecordedMimeType(mimeType);

      // Record via canvas to enforce a fixed 9:16 output.
      const sourceVideo = document.createElement("video");
      sourceVideo.srcObject = stream;
      sourceVideo.muted = true;
      sourceVideo.playsInline = true;
      sourceVideo.setAttribute("playsinline", "true");
      sourceVideo.setAttribute("webkit-playsinline", "true");
      sourceVideoRef.current = sourceVideo;

      if (sourceVideo.readyState < 1) {
        await new Promise<void>((resolve, reject) => {
          const timeout = setTimeout(
            () => reject(new Error("Video metadata timeout")),
            10000,
          );
          sourceVideo.onloadedmetadata = () => {
            clearTimeout(timeout);
            resolve();
          };
        });
      }
      await sourceVideo.play();

      const targetWidth = 720;
      const targetHeight = 1280;
      const targetAspect = targetWidth / targetHeight;
      const canvas = document.createElement("canvas");
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      const context = canvas.getContext("2d");
      if (!context) {
        throw new Error("Canvas 2D context unavailable");
      }

      const drawFrame = () => {
        const sourceWidth = sourceVideo.videoWidth;
        const sourceHeight = sourceVideo.videoHeight;

        if (sourceWidth > 0 && sourceHeight > 0) {
          const sourceAspect = sourceWidth / sourceHeight;
          let cropWidth = sourceWidth;
          let cropHeight = sourceHeight;
          let cropX = 0;
          let cropY = 0;

          if (sourceAspect > targetAspect) {
            cropWidth = sourceHeight * targetAspect;
            cropX = (sourceWidth - cropWidth) / 2;
          } else if (sourceAspect < targetAspect) {
            cropHeight = sourceWidth / targetAspect;
            cropY = (sourceHeight - cropHeight) / 2;
          }

          context.drawImage(
            sourceVideo,
            cropX,
            cropY,
            cropWidth,
            cropHeight,
            0,
            0,
            targetWidth,
            targetHeight,
          );
        }

        drawFrameRef.current = requestAnimationFrame(drawFrame);
      };

      drawFrame();

      const canvasStream = canvas.captureStream(30);
      for (const audioTrack of stream.getAudioTracks()) {
        canvasStream.addTrack(audioTrack);
      }
      recordingStreamRef.current = canvasStream;

      // Use MediaRecorder with bitrate settings for compression during recording
      // This is much faster than post-recording compression with FFmpeg
      const mediaRecorder = new MediaRecorder(canvasStream, {
        mimeType,
        videoBitsPerSecond: 1000000, // 1 Mbps video - good quality with small size
        audioBitsPerSecond: 128000, // 128 kbps audio
      });

      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        setRecordedVideo(blob);
        setVideoPreviewUrl(URL.createObjectURL(blob));
        console.log(
          `Video recorded: ${(blob.size / 1024 / 1024).toFixed(
            2,
          )} MB (${mimeType})`,
        );

        stopActiveRecordingStreams();
      };

      mediaRecorder.start();
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        stopActiveRecordingStreams();
        setIsRecording(false);
        return;
      }
      console.error("Error starting recording:", error);
      stopActiveRecordingStreams();
      setIsRecording(false);

      if (error instanceof DOMException) {
        switch (error.name) {
          case "NotAllowedError":
            alert(
              t("camera_permission_denied") ||
                "Camera access was denied. Please allow camera and microphone access in your browser settings and try again.",
            );
            break;
          case "NotFoundError":
            alert(
              t("camera_not_found") ||
                "No camera found on this device. Please connect a camera and try again.",
            );
            break;
          case "NotReadableError":
            alert(
              t("camera_in_use") ||
                "Camera is already in use by another app. Please close other apps using the camera and try again.",
            );
            break;
          case "OverconstrainedError":
            alert(
              t("camera_constraint_error") ||
                "Your camera does not support the required settings. Please try again.",
            );
            break;
          case "NotSupportedError":
            alert(
              t("camera_not_supported") ||
                "Camera access requires a secure connection (HTTPS). Please access the site via HTTPS and try again.",
            );
            break;
          default:
            alert(
              t("camera_generic_error") ||
                "Could not access camera. Please check your browser permissions and try again.",
            );
        }
      } else {
        alert(
          t("camera_generic_error") ||
            "Could not access camera. Please check your browser permissions and try again.",
        );
      }
    }
  };

  // Effect to connect stream to video element when recording starts
  useEffect(() => {
    if (isRecording && streamRef.current && videoRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch((error) => {
        if (!(error instanceof DOMException && error.name === "AbortError")) {
          console.error(error);
        }
      });
    }
  }, [isRecording]);

  // Effect to manage recording duration timer
  useEffect(() => {
    if (isRecording) {
      setRecordingDuration(0);
      recordingIntervalRef.current = setInterval(() => {
        setRecordingDuration((prev) => prev + 1);
      }, 1000);
    } else {
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
    }
    return () => {
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
    };
  }, [isRecording]);

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const discardVideo = () => {
    if (videoPreviewUrl) {
      URL.revokeObjectURL(videoPreviewUrl);
    }
    setRecordedVideo(null);
    setVideoPreviewUrl(null);
    stopActiveRecordingStreams();
  };

  const handleSubmitVideoApplication = async () => {
    if (!room || !recordedVideo) return;
    resetTurnstileToken();
    setPendingApplicationType("video");
    setShowTurnstileModal(true);
  };

  const doSubmitVideoApplication = async (token: string) => {
    if (!room || !recordedVideo) return;

    // Store video blob and room data before closing modal
    const videoToUpload = recordedVideo;
    const mimeType = recordedMimeType;
    const roomId = room.room_id;
    const flatName = flat?.name || "Flat";

    // Determine file extension based on mime type
    const fileExtension = mimeType.includes("mp4") ? "mp4" : "webm";
    const contentType = mimeType.includes("mp4") ? "video/mp4" : "video/webm";

    const isMobile = window.innerWidth < 1024;

    // On desktop, close modal so user can continue browsing
    // On mobile, keep the modal open to show upload progress
    if (!isMobile) {
      closeApplyModal();
    } else {
      setIsUploading(true);
      setUploadProgress(0);
    }

    // Start upload tracking in sidebar and show toast notification
    startUpload(flatName);
    showUploadToast(
      t("video_upload_started") ||
        "Your video application is being uploaded. You can continue browsing!",
    );
    updateProgress(5, t("getting_upload_url") || "Preparing upload...");
    if (isMobile) setUploadProgress(5);

    try {
      // Video is already compressed during recording with bitrate settings
      // No need for slow FFmpeg post-processing

      // 1. Get presigned URL for upload
      const { object_name, put_url } = await createObjectMutation.mutateAsync({
        object: `video.${fileExtension}`,
        kind: "video_application",
      });

      console.log("Got presigned URL:", put_url);
      console.log(
        `Uploading video: ${(videoToUpload.size / 1024 / 1024).toFixed(
          2,
        )} MB (${mimeType})`,
      );
      updateProgress(10, t("uploading") || "Uploading...");
      if (isMobile) setUploadProgress(10);

      // 2. Upload the video directly to S3
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const progress = 10 + (event.loaded / event.total) * 80;
            updateProgress(
              Math.round(progress),
              t("uploading") || "Uploading...",
            );
            if (isMobile) setUploadProgress(Math.round(progress));
          }
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(
              new Error(
                `Upload failed with status ${xhr.status}: ${xhr.statusText}`,
              ),
            );
          }
        };

        xhr.onerror = () => {
          console.error("XHR error:", xhr.status, xhr.statusText);
          reject(new Error("Network error during upload"));
        };

        xhr.open("PUT", put_url);
        xhr.setRequestHeader("Content-Type", contentType);
        xhr.send(videoToUpload);
      });

      updateProgress(90, t("finalizing") || "Finalizing...");
      if (isMobile) setUploadProgress(90);

      // 4. Create the video application
      setTurnstileToken(token);
      try {
        await createVideoApplicationMutation.mutateAsync({
          room_id: roomId,
          object_name: object_name,
        });
      } finally {
        clearTurnstileToken();
        resetTurnstileToken();
      }

      // Mark upload as complete
      completeUpload();
      posthog?.capture("application_submitted", {
        flat_id: flatId,
        type: "video",
      });
      userApplicationsQuery.refetch();

      if (isMobile) {
        setIsUploading(false);
        setApplicationSuccess(true);
      }
    } catch (error) {
      console.error("Error submitting video application:", error);
      const message =
        error instanceof Error ? error.message : "Failed to upload video.";
      failUpload(message);

      if (isMobile) {
        setIsUploading(false);
        setUploadProgress(0);
        closeApplyModal();
      }
    }
  };

  const closeApplyModal = () => {
    // Clean up video resources
    if (videoPreviewUrl) {
      URL.revokeObjectURL(videoPreviewUrl);
    }
    stopActiveRecordingStreams();

    setShowApplyModal(false);
    setApplicationSuccess(false);
    setApplicationText("");
    setApplicationType("text");
    setRecordedVideo(null);
    setVideoPreviewUrl(null);
    setIsRecording(false);
    setIsUploading(false);
    setUploadProgress(0);
  };

  const handleTurnstileSuccess = (token: string) => {
    setShowTurnstileModal(false);
    if (pendingApplicationType === "text") {
      doSubmitTextApplication(token);
    } else if (pendingApplicationType === "video") {
      doSubmitVideoApplication(token);
    }
    setPendingApplicationType(null);
  };

  // Reset drag state when modal closes
  useEffect(() => {
    if (!showApplyModal) {
      setHasUserPositioned(false);
      setIsModalDragging(false);
      modalDragRef.current = null;
    }
  }, [showApplyModal]);

  // Drag event listeners
  useEffect(() => {
    if (!isModalDragging) return;

    const handleMove = (e: MouseEvent | TouchEvent) => {
      if (!modalDragRef.current) return;
      e.preventDefault();
      const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
      const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;

      const dx = clientX - modalDragRef.current.startX;
      const dy = clientY - modalDragRef.current.startY;

      const newPos = {
        x: modalDragRef.current.startPosX + dx,
        y: Math.max(0, modalDragRef.current.startPosY + dy),
      };

      modalPosRef.current = newPos;
      if (modalRafRef.current) cancelAnimationFrame(modalRafRef.current);
      modalRafRef.current = requestAnimationFrame(() => setModalPos(newPos));
    };

    const handleEnd = () => {
      setIsModalDragging(false);

      if (applyModalRef.current) {
        const rect = applyModalRef.current.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const snapPadding = 16;
        const newX =
          centerX < window.innerWidth / 2
            ? snapPadding
            : window.innerWidth - rect.width - snapPadding;
        const maxY = window.innerHeight - 60;
        const newY = Math.max(16, Math.min(modalPosRef.current.y, maxY));

        const snapped = { x: newX, y: newY };
        modalPosRef.current = snapped;
        setModalPos(snapped);
      }

      modalDragRef.current = null;
    };

    window.addEventListener("mousemove", handleMove, { passive: false });
    window.addEventListener("mouseup", handleEnd);
    window.addEventListener("touchmove", handleMove, { passive: false });
    window.addEventListener("touchend", handleEnd);

    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleEnd);
      window.removeEventListener("touchmove", handleMove);
      window.removeEventListener("touchend", handleEnd);
      if (modalRafRef.current) cancelAnimationFrame(modalRafRef.current);
    };
  }, [isModalDragging]);

  // Drag start handler
  const handleModalDragStart = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      const clientX =
        "touches" in e
          ? (e as React.TouchEvent).touches[0].clientX
          : (e as React.MouseEvent).clientX;
      const clientY =
        "touches" in e
          ? (e as React.TouchEvent).touches[0].clientY
          : (e as React.MouseEvent).clientY;

      if (!applyModalRef.current) return;
      const rect = applyModalRef.current.getBoundingClientRect();

      if (!hasUserPositioned) {
        const pos = { x: rect.left, y: rect.top };
        modalPosRef.current = pos;
        setModalPos(pos);
        setHasUserPositioned(true);
      }

      modalDragRef.current = {
        startX: clientX,
        startY: clientY,
        startPosX: hasUserPositioned ? modalPosRef.current.x : rect.left,
        startPosY: hasUserPositioned ? modalPosRef.current.y : rect.top,
      };
      setIsModalDragging(true);
    },
    [hasUserPositioned],
  );

  // Skeleton loading component
  const SkeletonLoader = () => (
    <div
      className="min-h-screen flex flex-col font-gnarly-regular animate-pulse"
      style={{ backgroundColor: containerNeutralSolidSurface }}
    >
      {/* Header skeleton - starts after left sidebar on desktop */}
      <div
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isCollapsed ? "lg:left-20" : "lg:left-64"
        }`}
        style={{ backgroundColor: containerNeutralSolidSurface }}
      >
        <div className="max-w-[83rem] mx-auto px-4 sm:px-8 py-4 flex items-center justify-between ">
          <div className="hidden md:flex items-center gap-8">
            <div
              className="h-4 w-16 rounded"
              style={{ backgroundColor: containerNeutralLightMedium }}
            />
            <div
              className="h-4 w-16 rounded"
              style={{ backgroundColor: containerNeutralLightMedium }}
            />
            <div
              className="h-4 w-16 rounded"
              style={{ backgroundColor: containerNeutralLightMedium }}
            />
          </div>
          <div className="flex items-center gap-4 ml-auto">
            <div
              className="h-12 w-40 rounded-full"
              style={{ backgroundColor: containerNeutralLightMedium }}
            />
          </div>
        </div>
        <div
          className="h-[0.5px]"
          style={{ backgroundColor: containerNeutralLightMedium, opacity: 0.5 }}
        />
      </div>

      {/* Scrollable content area */}
      <div className="flex-1 pt-20 pb-24">
        <div className="max-w-[83rem] mx-auto px-4 sm:px-8 w-full">
          {/* Title skeleton */}
          <div className="pt-4">
            <div className="flex items-center justify-between gap-3 mb-3 pt-6">
              <div className="flex-1">
                <div
                  className="h-8 w-64 rounded mb-2"
                  style={{ backgroundColor: containerNeutralLightMedium }}
                />
                <div
                  className="h-4 w-40 rounded"
                  style={{ backgroundColor: containerNeutralLightMedium }}
                />
              </div>
            </div>
          </div>

          {/* Image collage skeleton */}
          <div className="pb-4">
            <div className="hidden md:grid grid-cols-4 grid-rows-2 gap-2 rounded-2xl overflow-hidden h-[600px]">
              <div
                className="col-span-2 row-span-2"
                style={{ backgroundColor: containerNeutralLightMedium }}
              />
              <div style={{ backgroundColor: containerNeutralLightMedium }} />
              <div style={{ backgroundColor: containerNeutralLightMedium }} />
              <div style={{ backgroundColor: containerNeutralLightMedium }} />
              <div style={{ backgroundColor: containerNeutralLightMedium }} />
            </div>
            <div
              className="md:hidden h-[300px] rounded-2xl"
              style={{ backgroundColor: containerNeutralLightMedium }}
            />
          </div>

          {/* Tags skeleton */}
          <div className="flex gap-2 mb-8">
            {[1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                className="h-8 w-20 rounded-full"
                style={{ backgroundColor: containerNeutralLightMedium }}
              />
            ))}
          </div>

          {/* Content skeleton */}
          <div className="flex flex-col gap-10">
            <div className="flex-1 space-y-6">
              <div
                className="h-4 w-full rounded"
                style={{ backgroundColor: containerNeutralLightMedium }}
              />
              <div
                className="h-4 w-3/4 rounded"
                style={{ backgroundColor: containerNeutralLightMedium }}
              />
              <div
                className="h-4 w-5/6 rounded"
                style={{ backgroundColor: containerNeutralLightMedium }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Redirect to browse page if flat not found
  useEffect(() => {
    if (!isLoading && (error || !flat || !room)) {
      navigate("/", { replace: true });
    }
  }, [isLoading, error, flat, room, navigate]);

  if (isLoading || error || !flat || !room) {
    return <SkeletonLoader />;
  }

  const totalPrice = room.rent + room.utilities;

  const address = flat.location.address;
  const city =
    address?.city || address?.town || address?.village || address?.suburb;

  const formatDate = (date: Date): string => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const fromDate = room.from_date ? formatDate(new Date(room.from_date)) : "";
  const toDate = room.permanent
    ? ""
    : room.to_date
      ? formatDate(new Date(room.to_date))
      : "";

  const ageRange =
    room.looking_for_age_range.max === MAX_AGE
      ? `${room.looking_for_age_range.min} - ${
          room.looking_for_age_range.max
        }+ ${t("years")}`
      : `${room.looking_for_age_range.min} - ${
          room.looking_for_age_range.max
        } ${t("years")}`;

  const images = stableImages;
  const mainImage = images[0];
  const sideImages = images.slice(1, 5);

  const openLightbox = (index: number) => {
    if (!images.length) return;
    setActiveImageIndex(index);
    setIsLightboxOpen(true);
  };

  const tagItems = [
    {
      key: "members",
      icon: "👥",
      label: flat.member_count.toString(),
    },
    {
      key: "ageRange",
      label: ageRange,
    },
    {
      key: "roomSize",
      label: `${room.size} m²`,
    },
    {
      key: "stayType",
      icon: "📅",
      label: room.permanent ? t("permanent") : t("temporary"),
    },
    {
      key: "lookingFor",
      icon: "🔍",
      label: room.looking_for
        .map((gender: string) => t(gender) || gender)
        .join(", "),
    },
    {
      key: "furnishing",
      icon: "🛏️",
      label: t(room.furnishing),
    },
  ];

  return (
    <div
      className="min-h-screen flex flex-col font-gnarly-regular"
      style={{
        backgroundColor: containerNeutralSolidSurface,
        color: textNeutralDefault,
      }}
    >
      {/* Scrollable content area */}
      <div className="flex-1 pt-8 pb-24 ">
        {/* Shared width container for title, images and content */}
        <div className="max-w-[83rem] mx-auto px-4 sm:px-8 w-full box-border">
          {/* TITLE ROW */}
          <div className="pt-2">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3 pt-2">
              <div className="min-w-0 flex-1">
                <h1
                  className="text-left text-2xl sm:text-3xl font-semibold truncate m-0 leading-tight"
                  style={{ color: textNeutralDefault }}
                >
                  {flat.name}
                </h1>
                <p
                  className="text-left text-sm truncate m-0"
                  style={{ color: textNeutralMuted }}
                >
                  {city && <span>{city}</span>}
                  {city && address?.country && " · "}
                  {address?.country}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {/* Edit button - only for flat owners */}
                {isOwnFlat && (
                  <button
                    type="button"
                    onClick={() => navigate(`/app/edit-flat/${flatId}`)}
                    className="shrink-0 flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium hover:bg-black/5 transition-colors"
                    style={{ color: accentColor }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                    <span className="hidden sm:inline">
                      {t("edit") || "Edit"}
                    </span>
                  </button>
                )}

                {/* Save/Unsave button - hidden for own flats */}
                {user && !isOwnFlat && (
                  <button
                    type="button"
                    onClick={handleToggleSave}
                    disabled={
                      saveFlatMutation.isPending || unSaveFlatMutation.isPending
                    }
                    className="shrink-0 flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium hover:bg-black/5 transition-colors disabled:opacity-50"
                    style={{
                      color: isFlatSaved ? accentColor : textNeutralDefault,
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill={isFlatSaved ? "currentColor" : "none"}
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                    </svg>
                    <span className="hidden sm:inline">
                      {isFlatSaved
                        ? t("saved") || "Saved"
                        : t("save") || "Save"}
                    </span>
                  </button>
                )}

                {/* Share button */}
                <button
                  type="button"
                  onClick={() => setIsShareModalOpen(true)}
                  className="shrink-0 flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium hover:bg-black/5 transition-colors"
                  style={{ color: textNeutralDefault }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                    <polyline points="16 6 12 2 8 6" />
                    <line x1="12" y1="2" x2="12" y2="15" />
                  </svg>
                  <span className="hidden sm:inline">
                    {t("share") || "Share"}
                  </span>
                </button>

                {user && !isOwnFlat && safetyTargetUserIds.length > 0 && (
                  <div className="relative" ref={safetyMenuRef}>
                    <button
                      type="button"
                      onClick={() => setShowSafetyMenu((prev) => !prev)}
                      className="shrink-0 flex items-center justify-center w-10 h-10 rounded-lg text-sm font-medium hover:bg-black/5 transition-colors"
                      style={{ color: textNeutralDefault }}
                      title="More options"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="12" cy="12" r="1" />
                        <circle cx="19" cy="12" r="1" />
                        <circle cx="5" cy="12" r="1" />
                      </svg>
                    </button>
                    {showSafetyMenu && (
                      <div
                        className="absolute right-0 top-full mt-2 rounded-xl overflow-hidden shadow-xl z-20 min-w-[180px]"
                        style={{
                          backgroundColor: containerNeutralSolidSurface,
                          border: `1px solid ${containerNeutralLightMedium}`,
                        }}
                      >
                        <button
                          type="button"
                          onClick={handleBlockFlatmates}
                          disabled={blockUsersMutation.isPending}
                          className="w-full px-3 py-2 text-sm text-left hover:bg-black/5 transition-colors disabled:opacity-50"
                          style={{ color: textNeutralDefault }}
                        >
                          {blockUsersMutation.isPending
                            ? "Blocking..."
                            : "Block"}
                        </button>
                        <button
                          type="button"
                          onClick={handleOpenReportModal}
                          disabled={reportUsersMutation.isPending}
                          className="w-full px-3 py-2 text-sm text-left hover:bg-black/5 transition-colors disabled:opacity-50"
                          style={{ color: textNeutralDefault }}
                        >
                          Report
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            {safetyActionMessage && (
              <p className="mt-2 text-xs" style={{ color: textNeutralMuted }}>
                {safetyActionMessage}
              </p>
            )}
          </div>

          {/* IMAGE COLLAGE */}
          <div className="pb-4">
            <div className="relative">
              {/* Desktop Grid View */}
              <div
                className="
                hidden md:grid grid-cols-2 md:grid-cols-4 md:grid-rows-2 gap-2
                rounded-2xl overflow-hidden
                md:h-[600px]
              "
              >
                {/* Big main image on the left */}
                {mainImage && (
                  <button
                    type="button"
                    className="overflow-hidden focus:outline-none md:col-span-2 md:row-span-2"
                    onClick={() => openLightbox(0)}
                  >
                    <img
                      className="w-full h-full object-cover transition-transform duration-300 ease-out hover:scale-105"
                      src={mainImage}
                      alt="Flat main"
                    />
                  </button>
                )}

                {/* Side images on the right - adjust column/row spans based on count */}
                {sideImages.map((img, idx) => {
                  // 1 side image: spans 2 cols and 2 rows (fills right side)
                  // 2 side images: both span 2 cols (stacked vertically)
                  // 3 side images: first spans 2 cols, other two span 1 col each
                  // 4 side images: all span 1 col
                  const shouldSpanTwoCols =
                    sideImages.length === 1 ||
                    sideImages.length === 2 ||
                    (sideImages.length === 3 && idx === 0);
                  const shouldSpanTwoRows = sideImages.length === 1;

                  return (
                    <button
                      type="button"
                      key={img + idx}
                      className={`overflow-hidden focus:outline-none ${
                        shouldSpanTwoCols ? "md:col-span-2" : ""
                      } ${shouldSpanTwoRows ? "md:row-span-2" : ""}`}
                      onClick={() => openLightbox(idx + 1)}
                    >
                      <img
                        className="w-full h-full object-cover transition-transform duration-300 ease-out hover:scale-105"
                        src={img}
                        alt={`Flat image ${idx + 2}`}
                      />
                    </button>
                  );
                })}
              </div>

              {/* Mobile Carousel View */}
              <div className="md:hidden relative h-[300px] sm:h-[320px] rounded-2xl overflow-hidden group">
                <div
                  ref={carouselRef}
                  onScroll={handleScroll}
                  className="flex overflow-x-auto snap-x snap-mandatory h-full w-full scrollbar-hide"
                >
                  {images.map((img, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className="flex-shrink-0 w-full h-full snap-center focus:outline-none"
                      onClick={() => openLightbox(idx)}
                    >
                      <img
                        src={img}
                        alt={`Flat image ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>

                {/* Dots Indicator */}
                <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2 z-10 pointer-events-none">
                  {images.map((_, idx) => (
                    <div
                      key={idx}
                      className="w-2 h-2 rounded-full transition-all duration-300"
                      style={{
                        backgroundColor:
                          idx === currentScrollIndex
                            ? accentColor
                            : "rgba(255, 255, 255, 0.5)",
                        transform:
                          idx === currentScrollIndex
                            ? "scale(1.2)"
                            : "scale(1)",
                      }}
                    />
                  ))}
                </div>
              </div>

              {/* Price pill over collage */}
              <div className="absolute left-3 bottom-0 z-20">
                <div
                  className="px-4 rounded-full"
                  style={{
                    backgroundColor: containerTertiarySolidLow,
                  }}
                >
                  <p
                    className="text font-medium py-[5px]"
                    style={{ color: textTertiaryOnLow }}
                  >
                    {flat.location.currency} {totalPrice}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* MAIN CONTENT – TWO COLUMNS (left: details/address, right: booking card) */}
          <main>
            <section className="flex flex-wrap items-center gap-1 ">
              {tagItems.map((tag) => (
                <Tag key={tag.key} icon={tag.icon} label={tag.label} />
              ))}
            </section>

            {/* Details section */}

            <div className="mt-8 flex flex-col gap-10 lg:flex-row lg:gap-24">
              {/* LEFT COLUMN: users, description, details, map */}
              <div className="flex flex-col gap-4 flex-1 min-w-0">
                {/* Mobile inline apply buttons */}
                {!isOwnFlat && (
                  <section className="lg:hidden">
                    {hasAlreadyApplied ? (
                      <div
                        className="w-full rounded-2xl px-5 py-4 text-[15px] font-gnarly-semibold flex items-center justify-center gap-2"
                        style={{
                          backgroundColor: `${accentColor}14`,
                          color: accentColor,
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                        {t("already_applied") || "Already applied"}
                      </div>
                    ) : (
                      <div className="flex flex-col gap-2">
                        <button
                          type="button"
                          onClick={() => handleApplyClick("video")}
                          className="w-full rounded-full px-5 py-3.5 text-[15px] font-gnarly-semibold transition-all duration-150 active:scale-[0.98] flex items-center justify-center gap-2.5"
                          style={{
                            backgroundColor: accentColor,
                            color: textNeutralOnPrimary,
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polygon points="23 7 16 12 23 17 23 7" />
                            <rect
                              x="1"
                              y="5"
                              width="15"
                              height="14"
                              rx="2"
                              ry="2"
                            />
                          </svg>
                          {t("apply_with_video") || "Apply with Video"}
                        </button>
                        <p
                          className="text-[11px] font-medium flex items-center justify-center gap-1 mb-1"
                          style={{ color: accentColor }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                          </svg>
                          {t("video_better_chances") ||
                            "5x better chances with video"}
                        </p>
                        <button
                          type="button"
                          onClick={() => handleApplyClick("text")}
                          className="w-full rounded-full px-5 py-3 text-sm font-gnarly-semibold transition-all duration-150 active:scale-[0.98] flex items-center justify-center gap-2"
                          style={{
                            backgroundColor: "rgba(2, 2, 16, 0.05)",
                            color: textNeutralDefault,
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                          </svg>
                          {t("apply_with_text") || "Apply with Text"}
                        </button>
                      </div>
                    )}
                  </section>
                )}

                <section>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <h2
                      className="text-left text-sm sm:text-base font-semibold"
                      style={{ color: textNeutralDefault }}
                    >
                      {t("description")}
                    </h2>
                    {flat.flat_text && (
                      <TranslateButton
                        isTranslating={isTranslating}
                        showTranslation={showTranslation}
                        showLanguageSelector={showLanguageSelector}
                        selectedLanguage={selectedLanguage}
                        translatedText={translatedText}
                        onToggleTranslation={() => setShowTranslation(false)}
                        onToggleLanguageSelector={() =>
                          setShowLanguageSelector(!showLanguageSelector)
                        }
                        onSelectLanguage={(langCode) =>
                          translateText(flat.flat_text, langCode)
                        }
                        onCloseLanguageSelector={() =>
                          setShowLanguageSelector(false)
                        }
                      />
                    )}
                  </div>
                  <div
                    className="max-w-full overflow-hidden"
                    style={{ color: textNeutralMuted }}
                  >
                    {translationError && (
                      <p
                        className="text-xs mb-3 px-4 py-2.5 rounded-2xl"
                        style={{
                          backgroundColor: "rgba(220, 54, 46, 0.07)",
                          color: "#B02B25",
                        }}
                      >
                        {translationError}
                      </p>
                    )}
                    <p
                      className="text-sm sm:text-base whitespace-pre-line break-words overflow-wrap-anywhere"
                      style={{
                        color: textNeutralDefault,
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                      }}
                    >
                      {showTranslation && translatedText
                        ? translatedText
                        : flat.flat_text || "No description provided yet."}
                    </p>
                    {showTranslation && translatedText && (
                      <p
                        className="mt-2 text-[10px] italic"
                        style={{ color: textNeutralMuted }}
                      >
                        {t("translated_by_google") || "Translated by Google"}
                      </p>
                    )}
                    <p
                      className="mt-3 text-[11px] flex items-center gap-2"
                      style={{ color: textNeutralMuted }}
                    >
                      {t("created_at") || "Created at"}{" "}
                      {formatDate(new Date(flat.created_at))}
                      {flat.has_applied && (
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide"
                          style={{
                            backgroundColor: containerTertiarySolidLow,
                            color: accentColor,
                            border: `1px solid ${accentColor}`,
                          }}
                        >
                          {t("you_applied") || "You applied"}
                        </span>
                      )}
                    </p>
                  </div>
                </section>

                {/* Details */}
                <section ref={detailsSectionRef}>
                  <h2
                    className="text-left text-sm sm:text-base font-semibold"
                    style={{ color: textNeutralDefault }}
                  >
                    {t("fullscreencard.details.title")}
                  </h2>

                  <div
                    className="py-3 flex flex-col sm:flex-row gap-3 sm:gap-6"
                    style={{
                      borderTop: `1px solid ${containerNeutralLightMedium}`,
                    }}
                  >
                    <div className="flex-1">
                      <p
                        className="text-xs mb-1 text-left"
                        style={{ color: textNeutralMuted }}
                      >
                        {t("fullscreencard.details.rent")}
                      </p>
                      <p
                        className="text-sm sm:text-base text-left"
                        style={{ color: textNeutralDefault }}
                      >
                        {flat.location.currency} {room.rent}
                      </p>
                    </div>
                    <div className="flex-1">
                      <p
                        className="text-xs mb-1 text-left"
                        style={{ color: textNeutralMuted }}
                      >
                        {t("fullscreencard.details.utilities")}
                      </p>
                      <p
                        className="text-sm sm:text-base text-left"
                        style={{ color: textNeutralDefault }}
                      >
                        {flat.location.currency} {room.utilities}
                      </p>
                    </div>
                  </div>

                  <div
                    className="py-3 flex flex-col sm:flex-row gap-3 sm:gap-6"
                    style={{
                      borderTop: `1px solid ${containerNeutralLightMedium}`,
                    }}
                  >
                    <div className="flex-1">
                      <p
                        className="text-xs mb-1 text-left"
                        style={{ color: textNeutralMuted }}
                      >
                        {t("fullscreencard.details.deposit")}
                      </p>
                      <p
                        className="text-sm sm:text-base text-left"
                        style={{ color: textNeutralDefault }}
                      >
                        {flat.location.currency} {room.deposit}
                      </p>
                    </div>
                    <div className="flex-1">
                      <p
                        className="text-xs mb-1 text-left"
                        style={{ color: textNeutralMuted }}
                      >
                        {t("fullscreencard.details.furnishing_fee")}
                      </p>
                      <p
                        className="text-sm sm:text-base text-left"
                        style={{ color: textNeutralDefault }}
                      >
                        {flat.location.currency} {room.equipment_fees}
                      </p>
                    </div>
                  </div>

                  <div
                    className="py-3 flex flex-col sm:flex-row gap-3 sm:gap-6"
                    style={{
                      borderTop: `1px solid ${containerNeutralLightMedium}`,
                    }}
                  >
                    {flat.size > 0 && (
                      <div className="flex-1">
                        <p
                          className="text-xs mb-1 text-left"
                          style={{ color: textNeutralMuted }}
                        >
                          {t("fullscreencard.details.flat_size")}
                        </p>
                        <p
                          className="text-sm sm:text-base text-left"
                          style={{ color: textNeutralDefault }}
                        >
                          {flat.size} m²
                        </p>
                      </div>
                    )}
                    <div className="flex-1">
                      <p
                        className="text-xs mb-1 text-left"
                        style={{ color: textNeutralMuted }}
                      >
                        {t("fullscreencard.details.room_size")}
                      </p>
                      <p
                        className="text-sm sm:text-base text-left"
                        style={{ color: textNeutralDefault }}
                      >
                        {room.size} m²
                      </p>
                    </div>
                  </div>

                  <div
                    className="py-3"
                    style={{
                      borderTop: `1px solid ${containerNeutralLightMedium}`,
                    }}
                  >
                    <p
                      className="text-xs mb-1 text-left"
                      style={{ color: textNeutralMuted }}
                    >
                      {t("fullscreencard.details.furnishing")}
                    </p>
                    <p
                      className="text-sm sm:text-base text-left"
                      style={{ color: textNeutralDefault }}
                    >
                      {t("fullscreencard.details.your_room_is")}{" "}
                      {t(room.furnishing)}
                    </p>
                  </div>

                  <div
                    className="py-3 flex flex-col sm:flex-row gap-3 sm:gap-6"
                    style={{
                      borderTop: `1px solid ${containerNeutralLightMedium}`,
                    }}
                  >
                    <div className="flex-1">
                      <p
                        className="text-xs mb-1 text-left"
                        style={{ color: textNeutralMuted }}
                      >
                        {t("fullscreencard.details.from")}
                      </p>
                      <p
                        className="text-sm sm:text-base text-left"
                        style={{ color: textNeutralDefault }}
                      >
                        {fromDate}
                      </p>
                    </div>
                    {!room.permanent && (
                      <div className="flex-1">
                        <p
                          className="text-xs mb-1 text-left"
                          style={{ color: textNeutralMuted }}
                        >
                          {t("fullscreencard.details.to")}
                        </p>
                        <p
                          className="text-sm sm:text-base text-left"
                          style={{ color: textNeutralDefault }}
                        >
                          {toDate}
                        </p>
                      </div>
                    )}
                  </div>

                  {room.subsidised && (
                    <div
                      className="py-3"
                      style={{
                        borderTop: `1px solid ${containerNeutralLightMedium}`,
                      }}
                    >
                      <p
                        className="text-xs mb-1 text-left"
                        style={{ color: textNeutralMuted }}
                      >
                        {t("fullscreencard.details.subsidized")}
                      </p>
                      <p
                        className="text-sm sm:text-base text-left"
                        style={{ color: accentColor }}
                      >
                        {t("fullscreencard.details.subsidized_text")}
                      </p>
                    </div>
                  )}
                </section>

                {/* Flat Attributes */}
                <FlatAttributesDisplay
                  selectedAttributes={
                    flat.flat_attributes
                      ?.map((attr: FlatAttributeValue) => {
                        if (attr && typeof attr === "object") {
                          return attr.attribute !== undefined
                            ? String(attr.attribute)
                            : "";
                        }
                        return attr === null ? "" : String(attr);
                      })
                      .filter((attributeId) => attributeId.length > 0) || null
                  }
                />

                {/* Flat Members Section */}
                <section className="mt-8">
                  <h2
                    className="text-lg font-semibold mb-4 text-left"
                    style={{ color: textNeutralDefault }}
                  >
                    {t("fullscreencard.flat_members") || "Flatmates"}
                  </h2>
                  {user ? (
                    // User is authenticated - show flat members
                    <div className="flex flex-wrap gap-3">
                      {flatMembersQuery.isLoading ? (
                        // Loading skeleton
                        Array.from({ length: flat.member_count || 2 }).map(
                          (_, index) => (
                            <div
                              key={index}
                              className="flex items-center gap-3 p-1 rounded-full animate-pulse"
                              style={{
                                border: `1px solid ${containerNeutralLightMedium}`,
                              }}
                            >
                              <div
                                className="w-10 h-10 rounded-full"
                                style={{
                                  backgroundColor: containerNeutralLightMedium,
                                }}
                              />
                              <div
                                className="w-20 h-4 rounded"
                                style={{
                                  backgroundColor: containerNeutralLightMedium,
                                }}
                              />
                            </div>
                          ),
                        )
                      ) : flatMembersQuery.data &&
                        flatMembersQuery.data.length > 0 ? (
                        flatMembersQuery.data.map((member) => (
                          <button
                            key={member.user_id}
                            onClick={() =>
                              navigate(`/app/profile/${member.user_id}`)
                            }
                            className="flex items-center gap-3 p-1 rounded-full transition-all hover:opacity-80"
                            style={{
                              border: `1px solid ${containerNeutralLightMedium}`,
                            }}
                          >
                            {member.profile_image ? (
                              <img
                                src={member.profile_image}
                                alt={member.username}
                                className="w-10 h-10 rounded-full object-cover"
                              />
                            ) : (
                              <div
                                className="w-10 h-10 rounded-full flex items-center justify-center text-base font-semibold"
                                style={{
                                  backgroundColor: accentColor,
                                  color: textNeutralOnPrimary,
                                }}
                              >
                                {member.username?.charAt(0).toUpperCase() ||
                                  "?"}
                              </div>
                            )}
                            <span
                              className="text-sm font-medium"
                              style={{ color: textNeutralDefault }}
                            >
                              {member.username}
                            </span>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              style={{ color: textNeutralMuted }}
                            >
                              <polyline points="9 18 15 12 9 6"></polyline>
                            </svg>
                          </button>
                        ))
                      ) : (
                        <p
                          className="text-sm"
                          style={{ color: textNeutralMuted }}
                        >
                          {t("no_flatmates_found") || "No flatmates found"}
                        </p>
                      )}
                    </div>
                  ) : (
                    // User is not authenticated - show login prompt
                    <button
                      onClick={() => setShowAuthModal(true)}
                      className="flex items-center gap-3 p-1 pr-2 rounded-full transition-all hover:opacity-80"
                      style={{
                        border: `1px solid ${containerNeutralLightMedium}`,
                      }}
                    >
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center"
                        style={{
                          backgroundColor: Colors.hoverBackground,
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          style={{ color: textNeutralMuted }}
                        >
                          <rect
                            x="3"
                            y="11"
                            width="18"
                            height="11"
                            rx="2"
                            ry="2"
                          />
                          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                        </svg>
                      </div>
                      <span
                        className="text-sm font-medium"
                        style={{ color: textNeutralDefault }}
                      >
                        {t("login_to_see_flatmates") ||
                          "Login to see flatmates"}
                      </span>
                    </button>
                  )}
                </section>
              </div>

              {/* RIGHT COLUMN: Apply card (hidden for own flats) */}
              {!isOwnFlat && (
                <div className="hidden lg:block w-[380px] flex-shrink-0">
                  <div
                    className="sticky top-8 overflow-hidden"
                    style={{
                      borderRadius: 24,
                      backgroundColor: containerNeutralSolidSurface,
                      boxShadow:
                        "0 12px 48px rgba(2, 2, 16, 0.1), 0 0 0 1px rgba(2, 2, 16, 0.04)",
                    }}
                  >
                    {hasAlreadyApplied ? (
                      /* Already Applied — celebratory state */
                      <div className="p-7 text-center">
                        <div
                          className="w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-4"
                          style={{
                            backgroundColor: `${accentColor}14`,
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="26"
                            height="26"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            style={{ color: accentColor }}
                          >
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        </div>
                        <h3
                          className="text-lg font-gnarly-semibold tracking-tight mb-1.5"
                          style={{ color: textNeutralDefault }}
                        >
                          {t("already_applied") || "Application Sent"}
                        </h3>
                        <p
                          className="text-sm leading-relaxed mb-5"
                          style={{ color: textNeutralMuted }}
                        >
                          {t("application_pending_message") ||
                            "Your application is with the flatmates. They'll get back to you soon!"}
                        </p>
                        <button
                          type="button"
                          onClick={() => navigate("/")}
                          className="w-full rounded-full px-5 py-3 text-sm font-gnarly-semibold transition-all duration-150 hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2"
                          style={{
                            backgroundColor: "rgba(2, 2, 16, 0.05)",
                            color: textNeutralDefault,
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                            <polyline points="9 22 9 12 15 12 15 22" />
                          </svg>
                          {t("browse_more_wgs") || "Browse more WGs"}
                        </button>
                      </div>
                    ) : (
                      /* Not yet applied — CTA state */
                      <>
                        {/* Illustration with subtle gradient overlay */}
                        <div
                          className="relative flex justify-center pt-6 pb-2 px-6"
                          style={{
                            background: `linear-gradient(180deg, ${accentColor}08 0%, transparent 100%)`,
                          }}
                        >
                          <img
                            src="/apply.png"
                            alt="Connect with flatmates"
                            className="w-44 h-auto"
                          />
                        </div>

                        <div className="px-6 pb-6">
                          <h3
                            className="text-lg font-gnarly-semibold tracking-tight mb-1.5 text-center"
                            style={{ color: textNeutralDefault }}
                          >
                            {t("connect_with_flatmates") ||
                              "Connect with your future flatmates"}
                          </h3>
                          <p
                            className="text-sm leading-relaxed mb-5 text-center"
                            style={{ color: textNeutralMuted }}
                          >
                            {t("introduce_yourself_message") ||
                              "Introduce yourself and start a conversation. Great flatmates start with a simple hello."}
                          </p>

                          {/* Primary: Video button */}
                          <button
                            type="button"
                            onClick={() => handleApplyClick("video")}
                            className="w-full rounded-full px-5 py-3.5 text-[15px] font-gnarly-semibold transition-all duration-150 hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2.5"
                            style={{
                              backgroundColor: accentColor,
                              color: textNeutralOnPrimary,
                            }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polygon points="23 7 16 12 23 17 23 7" />
                              <rect
                                x="1"
                                y="5"
                                width="15"
                                height="14"
                                rx="2"
                                ry="2"
                              />
                            </svg>
                            {t("apply_with_video") || "Apply with Video"}
                          </button>

                          {/* Video hint */}
                          <p
                            className="text-[11px] font-medium flex items-center justify-center gap-1 mt-2.5 mb-3"
                            style={{ color: accentColor }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="12"
                              height="12"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                            </svg>
                            {t("video_better_chances") ||
                              "5x better chances with video"}
                          </p>

                          {/* Secondary: Text button */}
                          <button
                            type="button"
                            onClick={() => handleApplyClick("text")}
                            className="w-full rounded-full px-5 py-3 text-sm font-gnarly-semibold transition-all duration-150 hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2"
                            style={{
                              backgroundColor: "rgba(2, 2, 16, 0.05)",
                              color: textNeutralDefault,
                            }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                            </svg>
                            {t("apply_with_text") || "Apply with Text"}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Address / Location - Full width like images */}
            <section ref={locationSectionRef} className="pt-8">
              <h2
                className="text-left text-sm sm:text-base font-semibold mb-3"
                style={{ color: textNeutralDefault }}
              >
                {t("address")}
              </h2>

              <div
                className="rounded-2xl overflow-hidden [&_.gm-style-cc]:!hidden [&_.gmnoprint]:!hidden [&_a[href*='google.com/maps']]:!hidden [&_a[href*='terms']]:!hidden"
                style={{ height: 400 }}
              >
                {isMapLoaded ? (
                  <GoogleMap
                    mapContainerStyle={{ width: "100%", height: "100%" }}
                    center={{ lat: flat.latitude, lng: flat.longitude }}
                    zoom={15}
                    onLoad={(mapInstance) => setMap(mapInstance)}
                    onUnmount={() => setMap(null)}
                    options={{
                      mapId: GOOGLE_MAPS_MAP_ID,
                      disableDefaultUI: true,
                      zoomControl: true,
                      mapTypeControl: false,
                      streetViewControl: false,
                      fullscreenControl: false,
                      scaleControl: false,
                      rotateControl: false,
                      panControl: false,
                      keyboardShortcuts: false,
                      styles:
                        GOOGLE_MAPS_MAP_ID === "DEMO_MAP_ID"
                          ? undefined
                          : MAP_STYLES,
                      clickableIcons: false,
                    }}
                  >
                    {room && (
                      <OverlayViewF
                        position={{ lat: flat.latitude, lng: flat.longitude }}
                        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                      >
                        <div
                          className="transform -translate-x-1/2 -translate-y-1/2"
                          style={{
                            backgroundColor: textNeutralDefault,
                            color: containerNeutralSolidSurface,
                            padding: "6px 10px",
                            borderRadius: "20px",
                            fontSize: "13px",
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                          }}
                        >
                          {flat.location.currency} {room.rent + room.utilities}
                        </div>
                      </OverlayViewF>
                    )}
                  </GoogleMap>
                ) : (
                  <div
                    className="w-full h-full flex items-center justify-center"
                    style={{ backgroundColor: containerNeutralLightMedium }}
                  >
                    <p style={{ color: textNeutralMuted }}>Loading map...</p>
                  </div>
                )}
              </div>
            </section>
          </main>
        </div>
        {/* End shared width container */}
      </div>
      {/* End scrollable content area */}

      <LightBox
        isLightboxOpen={isLightboxOpen}
        images={images}
        activeImageIndex={activeImageIndex}
        setActiveImageIndex={setActiveImageIndex}
        closeLightbox={() => {
          setActiveImageIndex(0);
          setIsLightboxOpen(false);
        }}
      />

      {/* Verify Email Modal */}
      {showVerifyEmailModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{
            backgroundColor: "rgba(2, 2, 16, 0.45)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
          }}
        >
          <div
            className="max-w-md w-full p-6"
            style={{
              backgroundColor: containerNeutralSolidSurface,
              borderRadius: 20,
              boxShadow:
                "0 24px 80px rgba(2, 2, 16, 0.2), 0 0 0 1px rgba(2, 2, 16, 0.05)",
            }}
          >
            {/* Email icon */}
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5"
              style={{ backgroundColor: `${accentColor}14` }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="26"
                height="26"
                viewBox="0 0 24 24"
                fill="none"
                stroke={accentColor}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="2" y="4" width="20" height="16" rx="2" />
                <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
              </svg>
            </div>

            <h2
              className="text-xl font-gnarly-semibold mb-2 text-center"
              style={{ color: textNeutralDefault }}
            >
              {t("verify_email_title") || "Verify your email"}
            </h2>
            <p
              className="text-sm leading-relaxed text-center mb-6"
              style={{ color: textNeutralMuted }}
            >
              {t("verify_email_desc") ||
                "You need to verify your email address before you can send applications. Check your inbox for a verification link."}
            </p>

            {verifyEmailSent && !verifyEmailError && (
              <div
                className="px-4 py-3 rounded-xl text-sm text-center mb-4"
                style={{
                  backgroundColor: "rgba(34, 197, 94, 0.08)",
                  color: "#16a34a",
                }}
              >
                {t("verify_email_sent") ||
                  "Verification email sent! Check your inbox."}
              </div>
            )}

            {verifyEmailError && (
              <div
                className="px-4 py-3 rounded-xl text-sm text-center mb-4"
                style={{
                  backgroundColor: "rgba(220, 54, 46, 0.07)",
                  color: "#B02B25",
                }}
              >
                {verifyEmailError}
              </div>
            )}

            <div className="flex flex-col gap-2.5">
              <button
                onClick={handleRefreshVerification}
                className="w-full py-3.5 rounded-2xl font-gnarly-semibold text-sm transition-all duration-150 hover:scale-[1.01] active:scale-[0.99]"
                style={{
                  backgroundColor: accentColor,
                  color: textNeutralOnPrimary,
                }}
              >
                {t("verify_email_done") || "I've verified my email"}
              </button>
              <button
                onClick={handleResendVerification}
                className="w-full py-3.5 rounded-2xl font-gnarly-semibold text-sm transition-all duration-150 hover:scale-[1.01] active:scale-[0.99]"
                style={{
                  backgroundColor: "rgba(2, 2, 16, 0.05)",
                  color: textNeutralDefault,
                }}
              >
                {t("verify_email_resend") || "Resend verification email"}
              </button>
              <button
                onClick={() => setShowVerifyEmailModal(false)}
                className="w-full py-3 text-sm transition-opacity hover:opacity-70"
                style={{ color: textNeutralMuted }}
              >
                {t("cancel") || "Cancel"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Apply Modal - Fullscreen on mobile, draggable on desktop */}
      {showApplyModal && (
        <div
          ref={applyModalRef}
          className={`fixed z-[100] inset-0 lg:inset-auto ${
            applicationType === "video" && flat?.flat_question?.trim()
              ? "lg:w-[min(90vw,48rem)]"
              : applicationType === "video"
                ? ""
                : "lg:w-[min(90vw,32rem)]"
          }`}
          style={{
            ...(typeof window !== "undefined" && window.innerWidth >= 1024
              ? hasUserPositioned
                ? {
                    left: modalPos.x,
                    top: modalPos.y,
                    transition: isModalDragging
                      ? "none"
                      : "left 0.4s cubic-bezier(0.16, 1, 0.3, 1), top 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
                  }
                : applicationType === "video"
                  ? {
                      left: 0,
                      right: 0,
                      top: 0,
                      bottom: 0,
                      margin: "auto",
                      height: "fit-content",
                    }
                  : {
                      right: 16,
                      bottom: 16,
                    }
              : {}),
            borderRadius:
              typeof window !== "undefined" && window.innerWidth >= 1024
                ? 24
                : 0,
            backgroundColor: containerNeutralSolidSurface,
            boxShadow:
              typeof window !== "undefined" && window.innerWidth >= 1024
                ? "0 24px 80px rgba(2, 2, 16, 0.2), 0 8px 24px rgba(2, 2, 16, 0.1), 0 0 0 1px rgba(2, 2, 16, 0.05)"
                : "none",
            animation:
              typeof window !== "undefined" && window.innerWidth >= 1024
                ? "scaleIn 0.22s cubic-bezier(0.16, 1, 0.3, 1) forwards"
                : undefined,
            maxHeight:
              typeof window !== "undefined" && window.innerWidth >= 1024
                ? "90vh"
                : "100vh",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            ...(applicationType === "video" &&
            !flat?.flat_question?.trim() &&
            typeof window !== "undefined" &&
            window.innerWidth >= 1024
              ? {
                  width: "min(90vw, 22rem)",
                }
              : {}),
          }}
        >
          {/* Header — simple on mobile, drag handle on desktop */}
          <div
            className="flex items-center justify-between px-5 pt-3.5 pb-1 select-none shrink-0 lg:cursor-grab"
            style={{
              cursor:
                typeof window !== "undefined" && window.innerWidth >= 1024
                  ? isModalDragging
                    ? "grabbing"
                    : "grab"
                  : "default",
            }}
            onMouseDown={
              typeof window !== "undefined" && window.innerWidth >= 1024
                ? handleModalDragStart
                : undefined
            }
            onTouchStart={
              typeof window !== "undefined" && window.innerWidth >= 1024
                ? handleModalDragStart
                : undefined
            }
          >
            <div className="flex-1" />
            <div
              className="w-9 h-1 rounded-full hidden lg:block"
              style={{ backgroundColor: "rgba(2, 2, 16, 0.12)" }}
            />
            <div className="flex-1 flex justify-end">
              <button
                type="button"
                onClick={closeApplyModal}
                className="w-7 h-7 flex items-center justify-center rounded-full transition-all duration-150 hover:scale-110 active:scale-95"
                style={{
                  backgroundColor: "rgba(2, 2, 16, 0.06)",
                  color: textNeutralMuted,
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div
            className={`px-5 pb-5 flex-1 min-h-0 ${
              applicationType === "video" && !applicationSuccess && !isUploading
                ? "flex flex-col overflow-hidden"
                : "overflow-y-auto"
            }`}
          >
            {applicationSuccess ? (
              // Success state
              <div className="text-center py-8">
                <div
                  className="w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-4"
                  style={{
                    backgroundColor: `${accentColor}14`,
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="28"
                    height="28"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ color: accentColor }}
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <h2
                  className="text-xl font-gnarly-semibold mb-2 tracking-tight"
                  style={{ color: textNeutralDefault }}
                >
                  {t("application_sent") || "Application Sent!"}
                </h2>
                <p
                  className="text-sm leading-relaxed mb-6 max-w-xs mx-auto"
                  style={{ color: textNeutralMuted }}
                >
                  {applicationType === "video"
                    ? t("video_application_sent_description") ||
                      "Your video application has been submitted and is being processed. The flat members will review it and get back to you."
                    : t("application_sent_description") ||
                      "Your application has been submitted successfully. The flat members will review it and get back to you."}
                </p>
                <button
                  type="button"
                  onClick={closeApplyModal}
                  className="rounded-full px-8 py-3 text-sm font-gnarly-semibold transition-all duration-150 hover:scale-[1.02] active:scale-[0.98]"
                  style={{
                    backgroundColor: accentColor,
                    color: textNeutralOnPrimary,
                  }}
                >
                  {t("done") || "Done"}
                </button>
              </div>
            ) : isUploading ? (
              // Uploading state
              <div className="text-center py-8">
                <div
                  className="w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-4"
                  style={{
                    backgroundColor: `${accentColor}14`,
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="28"
                    height="28"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ color: accentColor }}
                    className="animate-spin"
                  >
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                  </svg>
                </div>
                <h2
                  className="text-xl font-gnarly-semibold mb-2 tracking-tight"
                  style={{ color: textNeutralDefault }}
                >
                  {t("uploading_video") || "Uploading Video..."}
                </h2>
                <p
                  className="text-sm mb-5 leading-relaxed"
                  style={{ color: textNeutralMuted }}
                >
                  {t("please_wait") ||
                    "Please wait while we upload your video application."}
                </p>
                <div
                  className="w-full h-1.5 rounded-full overflow-hidden"
                  style={{ backgroundColor: "rgba(2, 2, 16, 0.06)" }}
                >
                  <div
                    className="h-full rounded-full transition-all duration-300"
                    style={{
                      backgroundColor: accentColor,
                      width: `${uploadProgress}%`,
                    }}
                  />
                </div>
                <p
                  className="text-xs mt-2 font-medium"
                  style={{ color: textNeutralMuted }}
                >
                  {uploadProgress}%
                </p>
              </div>
            ) : (
              // Application form
              <>
                {applicationType === "text" ? (
                  // Text Application Form
                  <>
                    {flat?.flat_question && flat.flat_question.trim() !== "" ? (
                      <div
                        className="mb-4 p-4 rounded-2xl"
                        style={{
                          backgroundColor: "rgba(2, 2, 16, 0.03)",
                        }}
                      >
                        <p
                          className="text-[10px] font-gnarly-semibold uppercase tracking-widest mb-1.5"
                          style={{ color: textNeutralMuted }}
                        >
                          {t("flat_question_label") || "Question from the flat"}
                        </p>
                        <p
                          className="text-[15px] font-medium leading-snug"
                          style={{ color: textNeutralDefault }}
                        >
                          {flat.flat_question}
                        </p>
                      </div>
                    ) : (
                      <p
                        className="text-sm mb-4 leading-relaxed"
                        style={{ color: textNeutralMuted }}
                      >
                        {t("application_description") ||
                          "Write a message introducing yourself to the flat. Tell them about yourself, why you're interested, and when you'd like to move in."}
                      </p>
                    )}

                    <div className="relative">
                      <textarea
                        value={applicationText}
                        onChange={(e) => setApplicationText(e.target.value)}
                        placeholder={
                          t("application_placeholder") ||
                          "Hi! I'm interested in your flat because..."
                        }
                        className="w-full h-56 p-4 pb-14 rounded-2xl text-base resize-none focus:outline-none transition-shadow duration-150"
                        style={{
                          backgroundColor: "rgba(2, 2, 16, 0.03)",
                          color: textNeutralDefault,
                          border: "1px solid rgba(2, 2, 16, 0.08)",
                        }}
                        onFocus={(e) => {
                          e.currentTarget.style.boxShadow = `0 0 0 2px ${accentColor}33`;
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.boxShadow = "none";
                        }}
                      />
                      <div className="absolute bottom-[13px] right-[6px]">
                        <button
                          type="button"
                          onClick={handleSubmitTextApplication}
                          disabled={
                            !applicationText.trim() ||
                            createTextApplicationMutation.isPending
                          }
                          className="rounded-full px-4 py-2 text-xs font-gnarly-semibold transition-all duration-150 hover:scale-[1.03] active:scale-[0.97] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
                          style={{
                            backgroundColor: accentColor,
                            color: textNeutralOnPrimary,
                          }}
                        >
                          {createTextApplicationMutation.isPending
                            ? t("sending") || "Sending..."
                            : t("send_application") || "Send"}
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  // Video Application Form
                  <div
                    className={`flex flex-col flex-1 min-h-0 ${
                      flat?.flat_question?.trim() ? "md:flex-row md:gap-5" : ""
                    }`}
                  >
                    {/* Left side - Video area and controls */}
                    <div
                      className={`flex flex-col flex-1 min-h-0 ${
                        flat?.flat_question?.trim() ? "md:w-1/2" : ""
                      }`}
                    >
                      {!flat?.flat_question?.trim() && (
                        <p
                          className="text-sm mb-3 leading-relaxed shrink-0"
                          style={{ color: textNeutralMuted }}
                        >
                          {t("video_application_description") ||
                            "Record a short video introducing yourself. Tell them about yourself, why you're interested, and when you'd like to move in."}
                        </p>
                      )}

                      {/* Video Preview / Recording Area */}
                      <div
                        className="relative overflow-hidden flex-1 min-h-0 mx-auto"
                        style={{
                          backgroundColor: "rgba(2, 2, 16, 0.04)",
                          borderRadius: 20,
                          aspectRatio: "9 / 16",
                          maxHeight: "70vh",
                        }}
                      >
                        {isRecording ? (
                          <>
                            <video
                              ref={videoRef}
                              className="w-full h-full object-cover"
                              style={{ transform: "scaleX(-1)" }}
                              autoPlay
                              muted
                              playsInline
                            />
                            <div
                              className="absolute top-3 left-3 z-10 flex items-center gap-2 px-3 py-1.5 rounded-full text-white text-xs font-gnarly-semibold"
                              style={{
                                backgroundColor: "rgba(220, 54, 46, 0.9)",
                                backdropFilter: "blur(8px)",
                              }}
                            >
                              <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                              {String(
                                Math.floor(recordingDuration / 60),
                              ).padStart(2, "0")}
                              :{String(recordingDuration % 60).padStart(2, "0")}
                            </div>
                          </>
                        ) : videoPreviewUrl ? (
                          <>
                            <video
                              src={videoPreviewUrl}
                              className="w-full h-full object-cover"
                              playsInline
                              autoPlay
                              loop
                            />
                            <button
                              type="button"
                              onClick={discardVideo}
                              className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full flex items-center justify-center transition-all duration-150 hover:scale-110 active:scale-95"
                              style={{
                                backgroundColor: "rgba(0, 0, 0, 0.5)",
                                backdropFilter: "blur(8px)",
                                color: "white",
                              }}
                              title={t("re_record") || "Re-record"}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                                <path d="M3 3v5h5" />
                              </svg>
                            </button>
                          </>
                        ) : (
                          <div
                            className="w-full flex flex-col items-center justify-center p-4"
                            style={{ aspectRatio: "9 / 16" }}
                          >
                            <img
                              src="/person_waving.png"
                              alt="Person waving"
                              className="w-full h-full object-contain"
                            />
                          </div>
                        )}
                      </div>

                      {/* Recording Controls */}
                      <div className="flex gap-2.5 justify-center mt-3 shrink-0">
                        {isRecording ? (
                          <button
                            type="button"
                            onClick={stopRecording}
                            className="flex-1 rounded-full px-5 py-3 text-sm font-gnarly-semibold transition-all duration-150 hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2"
                            style={{
                              backgroundColor: "#dc362e",
                              color: "white",
                            }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="currentColor"
                              stroke="none"
                            >
                              <rect x="6" y="6" width="12" height="12" rx="2" />
                            </svg>
                            {t("stop_recording") || "Stop Recording"}
                          </button>
                        ) : videoPreviewUrl ? (
                          <button
                            type="button"
                            onClick={handleSubmitVideoApplication}
                            className="flex-1 rounded-full px-5 py-3 text-sm font-gnarly-semibold transition-all duration-150 hover:scale-[1.02] active:scale-[0.98]"
                            style={{
                              backgroundColor: accentColor,
                              color: textNeutralOnPrimary,
                            }}
                          >
                            {t("send_video") || "Send Video"}
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={startRecording}
                            className="flex-1 rounded-full px-5 py-3 text-sm font-gnarly-semibold transition-all duration-150 hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2"
                            style={{
                              backgroundColor: accentColor,
                              color: textNeutralOnPrimary,
                            }}
                          >
                            {t("start_recording") || "Start Recording"}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Right side - Question (only on desktop when question exists) */}
                    {flat?.flat_question?.trim() && (
                      <div className="mt-4 md:mt-0 md:w-1/2 flex flex-col">
                        <p
                          className="text-[10px] font-gnarly-semibold uppercase tracking-widest mb-2"
                          style={{ color: textNeutralMuted }}
                        >
                          {t("flat_question_label") || "Question from the flat"}
                        </p>
                        <p
                          className="text-[15px] font-medium leading-snug"
                          style={{ color: textNeutralDefault }}
                        >
                          {flat.flat_question}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {showReportModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/50">
          <div
            className="w-full max-w-md rounded-2xl p-5"
            style={{ backgroundColor: containerNeutralSolidSurface }}
          >
            <h3
              className="text-lg font-semibold mb-2"
              style={{ color: textNeutralDefault }}
            >
              Report flatmates
            </h3>
            <p className="text-sm mb-4" style={{ color: textNeutralMuted }}>
              Select a reason for reporting.
            </p>
            <div className="space-y-2 mb-4">
              {REPORT_REASONS.map((reason) => (
                <button
                  key={reason.key}
                  type="button"
                  onClick={() => {
                    setSelectedReportReason(reason.key);
                    if (reason.key !== "other") {
                      setCustomReportReason("");
                    }
                  }}
                  className="w-full text-left px-3 py-2 rounded-lg transition-colors"
                  style={{
                    border: `1px solid ${containerNeutralLightMedium}`,
                    backgroundColor:
                      selectedReportReason === reason.key
                        ? containerNeutralLightLowest
                        : "transparent",
                    color: textNeutralDefault,
                  }}
                >
                  {reason.label}
                </button>
              ))}
            </div>
            {selectedReportReason === "other" && (
              <textarea
                value={customReportReason}
                onChange={(event) => setCustomReportReason(event.target.value)}
                placeholder="Enter your reason"
                rows={4}
                className="w-full p-3 rounded-lg mb-4 resize-none outline-none"
                style={{
                  border: `1px solid ${containerNeutralLightMedium}`,
                  color: textNeutralDefault,
                  backgroundColor: containerNeutralSolidSurface,
                }}
              />
            )}
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setShowReportModal(false)}
                className="px-4 py-2 rounded-lg"
                style={{
                  backgroundColor: containerNeutralLightLowest,
                  color: textNeutralDefault,
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmitReport}
                disabled={reportUsersMutation.isPending}
                className="px-4 py-2 rounded-lg disabled:opacity-50"
                style={{
                  backgroundColor: accentColor,
                  color: textNeutralOnPrimary,
                }}
              >
                {reportUsersMutation.isPending ? "Reporting..." : "Report"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Turnstile verification modal */}
      {showTurnstileModal && (
        <div
          className="fixed inset-0 z-[150] flex items-center justify-center p-4"
          style={{
            backgroundColor: "rgba(2, 2, 16, 0.45)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
          }}
        >
          <div
            className="max-w-sm w-full p-6"
            style={{
              backgroundColor: containerNeutralSolidSurface,
              borderRadius: 20,
              boxShadow:
                "0 24px 80px rgba(2, 2, 16, 0.2), 0 0 0 1px rgba(2, 2, 16, 0.05)",
            }}
          >
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5"
              style={{ backgroundColor: `${accentColor}14` }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="26"
                height="26"
                viewBox="0 0 24 24"
                fill="none"
                stroke={accentColor}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>

            <h2
              className="text-xl font-gnarly-semibold mb-2 text-center"
              style={{ color: textNeutralDefault }}
            >
              {t("verify_human_title") || "Quick verification"}
            </h2>
            <p
              className="text-sm leading-relaxed text-center mb-6"
              style={{ color: textNeutralMuted }}
            >
              {t("verify_human_desc_application") ||
                "Please complete the check below to send your application."}
            </p>

            {turnstileError ? (
              <div className="flex flex-col items-center gap-3 mb-4">
                <p
                  className="text-sm text-center"
                  style={{ color: "#B02B25" }}
                >
                  {t("verify_human_error") ||
                    "Verification failed. Please try again."}
                </p>
                <button
                  onClick={resetTurnstileToken}
                  className="px-6 py-2.5 rounded-full text-sm font-gnarly-semibold transition-all hover:scale-[1.02] active:scale-[0.98]"
                  style={{
                    backgroundColor: accentColor,
                    color: textNeutralOnPrimary,
                  }}
                >
                  {t("try_again") || "Try again"}
                </button>
              </div>
            ) : (
              <div className="flex justify-center mb-4">
                <TurnstileWidget
                  siteKey={siteKey}
                  widgetRef={widgetRef}
                  onSuccess={handleTurnstileSuccess}
                  onExpire={onTurnstileExpire}
                  onError={onTurnstileError}
                  appearance="always"
                />
              </div>
            )}

            <button
              onClick={() => {
                setShowTurnstileModal(false);
                setPendingApplicationType(null);
                resetTurnstileToken();
              }}
              className="w-full py-3 text-sm transition-opacity hover:opacity-70"
              style={{ color: textNeutralMuted }}
            >
              {t("cancel") || "Cancel"}
            </button>
          </div>
        </div>
      )}

      {/* Auth Modal for non-logged-in users */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={() => {
          setShowAuthModal(false);
          setShowApplyModal(true);
        }}
      />

      {/* Share Modal */}
      <ShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        flat={{
          flat_id: flat.flat_id,
          name: flat.name,
          location: flat.location,
          member_count: flat.member_count,
          size: flat.size,
          room: flat.room,
          flat_text: flat.flat_text,
        }}
      />

      {/* Download App Modal – full-screen on mobile, shown once on first apply */}
      {showDownloadAppModal && (
        <div
          className="fixed inset-0 z-[200] flex flex-col"
          style={{ backgroundColor: accentColor }}
        >
          <style>{`
            @keyframes modalSlideUp {
              from { opacity: 0; transform: translateY(40px); }
              to { opacity: 1; transform: translateY(0); }
            }
            @keyframes imgBack {
              from { opacity: 0; transform: translate(-50%, -50%) rotate(0deg); }
              to { opacity: 1; transform: translate(-90%, -50%) rotate(-7deg); }
            }
            @keyframes imgFront {
              from { opacity: 0; transform: translate(-50%, -50%) rotate(0deg); }
              to { opacity: 1; transform: translate(-10%, -50%) rotate(7deg); }
            }
          `}</style>

          {/* Main content */}
          <div
            className="relative flex-1 flex flex-col items-center justify-center px-8 gap-6"
            style={{
              animation:
                "modalSlideUp 0.5s cubic-bezier(0.16,1,0.3,1) 0.1s both",
            }}
          >
            {/* Stacked two-image layout (same as about page) */}
            <div className="relative w-full h-[34vh] flex items-center justify-center">
              <img
                src="/home_flat_preview_english.png"
                alt="Flateroo browse"
                className="absolute left-1/2 top-1/2 h-[28vh] max-w-[80vw] w-auto object-contain drop-shadow-2xl z-[1]"
                style={{ animation: "imgBack 0.7s cubic-bezier(0.16,1,0.3,1) 0.2s both" }}
                draggable={false}
              />
              <img
                src="/flat_view_english.png"
                alt="Flateroo flat view"
                className="absolute left-1/2 top-1/2 h-[28vh] max-w-[80vw] w-auto object-contain drop-shadow-2xl z-[2]"
                style={{ animation: "imgFront 0.7s cubic-bezier(0.16,1,0.3,1) 0.35s both" }}
                draggable={false}
              />
            </div>

            {/* Messaging */}
            <div className="text-center max-w-xs">
              <h2
                className="text-3xl font-gnarly-bold tracking-tight leading-tight mb-3"
                style={{ color: "white" }}
              >
                {t("download_modal_title") || "Better on the app"}
              </h2>
              <p
                className="text-base leading-relaxed"
                style={{ color: "rgba(255,255,255,0.8)" }}
              >
                {t("download_modal_description") ||
                  "Apply with video, chat in real-time, and get notified when flats respond — all in the Flateroo app."}
              </p>
            </div>
          </div>

          {/* Bottom buttons */}
          <div
            className="relative px-6 pb-10 pt-4 flex flex-col gap-3"
            style={{
              animation:
                "modalSlideUp 0.5s cubic-bezier(0.16,1,0.3,1) 0.25s both",
            }}
          >
            {/* Download buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  posthog?.capture("download_app_modal_download_clicked", { flat_id: flatId, store: "app_store" });
                  window.location.href = "https://apps.apple.com/app/flateroo/id6683311358";
                }}
                className="flex-1 py-4 rounded-2xl text-sm font-gnarly-bold tracking-tight transition-all duration-150 active:scale-[0.97] flex items-center justify-center gap-2.5"
                style={{
                  backgroundColor: "white",
                  color: accentColor,
                  boxShadow: "0 4px 24px rgba(0,0,0,0.15)",
                }}
              >
                <img
                  src="/app-store-icon.svg"
                  alt=""
                  className="w-5 h-5"
                  style={{ filter: `brightness(0) saturate(100%)` }}
                />
                App Store
              </button>
              <button
                onClick={() => {
                  posthog?.capture("download_app_modal_download_clicked", { flat_id: flatId, store: "google_play" });
                  window.location.href = "https://play.google.com/store/apps/details?id=com.flateroo.flateroo";
                }}
                className="flex-1 py-4 rounded-2xl text-sm font-gnarly-bold tracking-tight transition-all duration-150 active:scale-[0.97] flex items-center justify-center gap-2.5"
                style={{
                  backgroundColor: "white",
                  color: accentColor,
                  boxShadow: "0 4px 24px rgba(0,0,0,0.15)",
                }}
              >
                <img src="/google-play-icon.svg" alt="" className="w-5 h-5" />
                Google Play
              </button>
            </div>

            {/* Continue on web link */}
            <button
              onClick={handleContinueOnWeb}
              className="w-full py-3 text-sm font-gnarly-semibold transition-all duration-150 active:scale-[0.97]"
              style={{ color: "rgba(255,255,255,0.7)" }}
            >
              {t("download_modal_skip") || "No thanks, continue on web"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FlatView;
